import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { driver_api } from '@/lib/api';
import { toast } from 'sonner';
import {
  SteeringWheel, SignOut, MapPin, NavigationArrow, Warning, CheckCircle, Clock
} from '@phosphor-icons/react';

const NEXT_ACTION = {
  assigned: { status: 'en_route', label: 'Start trip' },
  confirmed: { status: 'en_route', label: 'Start trip' },
  en_route: { status: 'arrived', label: 'Mark arrived' },
  arrived: { status: 'in_progress', label: 'Begin transport' },
  in_progress: { status: 'completed', label: 'Complete transport' },
};

const STATUS_STYLE = {
  assigned: 'bg-neutral-200 text-neutral-700',
  confirmed: 'bg-neutral-200 text-neutral-700',
  en_route: 'bg-blue-100 text-blue-700',
  arrived: 'bg-cyan-100 text-cyan-700',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
  no_show: 'bg-red-100 text-red-600',
};

function JobCard({ job, onUpdate }) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const action = NEXT_ACTION[job.status];

  const move = async (status) => {
    setBusy(true);
    try {
      await driver_api.updateJob(job.id, { status, driver_notes: notes || undefined });
      toast.success(`Updated: ${status.replace('_', ' ')}`);
      setNotes('');
      onUpdate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update.');
    } finally { setBusy(false); }
  };

  const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(job.pickup_address)}&destination=${encodeURIComponent(job.dropoff_address)}`;

  return (
    <div className="rounded-2xl bg-white border border-neutral-200 p-5" data-testid={`driver-job-${job.id}`}>
      <div className="flex items-center justify-between mb-3">
        <span className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[job.status] || 'bg-neutral-200'}`}>
          {job.status.replace('_', ' ')}
        </span>
        <span className="text-sm font-bold text-ink flex items-center gap-1"><Clock size={14} /> {job.pickup_time}</span>
      </div>
      <div className="font-display font-bold text-lg text-ink">{job.full_name}</div>
      <div className="text-xs text-ink-muted">{job.service_type} · {job.passengers} pax{job.wheelchair_accessible ? ' · wheelchair' : ''}</div>
      <div className="mt-3 space-y-1.5 text-sm">
        <div className="flex items-start gap-2"><MapPin size={15} className="text-brand mt-0.5 shrink-0" /> <span className="text-ink-soft">{job.pickup_address}</span></div>
        <div className="flex items-start gap-2"><NavigationArrow size={15} className="text-ink-muted mt-0.5 shrink-0" /> <span className="text-ink-soft">{job.dropoff_address}</span></div>
      </div>
      {job.special_requirements && (
        <div className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-amber-800">{job.special_requirements}</div>
      )}
      {job.status !== 'completed' && job.status !== 'no_show' && (
        <>
          <textarea
            value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            data-testid={`driver-job-notes-${job.id}`}
            className="input-base resize-none mt-4 !text-sm" placeholder="Trip notes (optional)"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid={`driver-job-navigate-${job.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-xs font-semibold text-ink-soft hover:border-brand hover:text-brand transition">
              <NavigationArrow size={14} /> Navigate
            </a>
            {action && (
              <button onClick={() => move(action.status)} disabled={busy} data-testid={`driver-job-action-${job.id}`} className="btn-primary !py-2 !px-4 text-xs">
                {busy ? '…' : action.label}
              </button>
            )}
            {['assigned', 'confirmed', 'en_route', 'arrived'].includes(job.status) && (
              <button onClick={() => window.confirm('Mark participant as no-show?') && move('no_show')} disabled={busy} data-testid={`driver-job-noshow-${job.id}`} className="inline-flex items-center gap-1 rounded-full border border-red-200 text-red-500 px-4 py-2 text-xs font-semibold hover:bg-red-50 transition">
                No-show
              </button>
            )}
          </div>
        </>
      )}
      {job.driver_notes && <div className="mt-3 text-xs text-ink-muted">Notes: {job.driver_notes}</div>}
    </div>
  );
}

function IncidentForm({ jobs, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ booking_id: '', incident_type: 'incident', description: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await driver_api.reportIncident(form);
      toast.success('Report sent to admin.');
      setForm({ booking_id: '', incident_type: 'incident', description: '' });
      setOpen(false);
      onDone?.();
    } catch { toast.error('Could not send report.'); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="driver-incident-open" className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-xs font-semibold text-ink-soft hover:border-red-400 hover:text-red-500 transition">
        <Warning size={14} /> Report an issue
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="p-5 rounded-2xl bg-white border border-neutral-200 w-full" data-testid="driver-incident-form">
      <div className="text-sm font-bold text-ink mb-3 flex items-center gap-2"><Warning size={16} className="text-red-500" /> Report an issue</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <select value={form.incident_type} onChange={e => setForm(f => ({ ...f, incident_type: e.target.value }))} data-testid="driver-incident-type" className="input-base !py-2 text-sm">
          <option value="incident">Incident</option>
          <option value="vehicle_defect">Vehicle defect</option>
          <option value="delay">Delay</option>
          <option value="no_show">Participant no-show</option>
          <option value="other">Other</option>
        </select>
        <select value={form.booking_id} onChange={e => setForm(f => ({ ...f, booking_id: e.target.value }))} data-testid="driver-incident-booking" className="input-base !py-2 text-sm">
          <option value="">No related job</option>
          {jobs.map(j => <option key={j.id} value={j.id}>{j.full_name} @ {j.pickup_time}</option>)}
        </select>
      </div>
      <textarea required value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} data-testid="driver-incident-description" className="input-base resize-none mt-3 !text-sm" placeholder="Describe what happened…" />
      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={busy} data-testid="driver-incident-submit" className="btn-primary !py-2 !px-4 text-xs">{busy ? 'Sending…' : 'Send report'}</button>
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-ink-muted">Cancel</button>
      </div>
    </form>
  );
}

export default function DriverPortal() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const load = async (all = showAll) => {
    setLoading(true);
    try {
      const res = await driver_api.jobs(all ? undefined : today);
      setJobs(res.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const token = localStorage.getItem('swift_driver_token');
    if (!token) { nav('/driver/login'); return; }
    driver_api.me().then(setMe).catch(() => nav('/driver/login'));
    load();
    // eslint-disable-next-line
  }, []);

  const toggleAll = () => { const v = !showAll; setShowAll(v); load(v); };

  const logout = () => { driver_api.logout(); nav('/driver/login'); };

  const active = jobs.filter(j => !['completed', 'no_show'].includes(j.status));
  const done = jobs.filter(j => ['completed', 'no_show'].includes(j.status));

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="driver-portal">
      <header className="bg-ink text-white px-5 py-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-brand flex items-center justify-center">
            <SteeringWheel size={18} weight="fill" />
          </div>
          <div>
            <div className="font-display font-bold text-sm leading-none">Inclusive Mobility Driver</div>
            <div className="text-[11px] text-white/60 mt-0.5" data-testid="driver-portal-name">{me?.full_name || '…'}</div>
          </div>
        </div>
        <button onClick={logout} data-testid="driver-logout" className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white">
          <SignOut size={14} /> Sign out
        </button>
      </header>

      <main className="p-5 max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="font-display font-extrabold text-2xl text-ink">{showAll ? 'All upcoming jobs' : "Today's jobs"}</h1>
            <p className="text-xs text-ink-muted mt-0.5">{today} · {active.length} active · {done.length} finished</p>
          </div>
          <button onClick={toggleAll} data-testid="driver-toggle-all" className="text-xs font-semibold text-brand">
            {showAll ? 'Show today only' : 'Show all upcoming'}
          </button>
        </div>

        <div className="mb-6"><IncidentForm jobs={jobs} /></div>

        {loading && <div className="text-ink-muted text-sm">Loading jobs…</div>}
        {!loading && jobs.length === 0 && (
          <div className="p-8 rounded-2xl bg-white border border-neutral-200 text-center text-ink-muted text-sm" data-testid="driver-no-jobs">
            No jobs {showAll ? 'assigned' : 'today'}. Enjoy the break!
          </div>
        )}

        <div className="space-y-4" data-testid="driver-jobs-list">
          {active.map(j => <JobCard key={j.id} job={j} onUpdate={load} />)}
        </div>

        {done.length > 0 && (
          <>
            <div className="flex items-center gap-2 mt-8 mb-3 text-sm font-semibold text-ink-muted">
              <CheckCircle size={16} className="text-green-500" /> Finished
            </div>
            <div className="space-y-4 opacity-70">
              {done.map(j => <JobCard key={j.id} job={j} onUpdate={load} />)}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
